import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT, PayoffEligibility, PayoffQuoteRequest, PayoffQuoteResponse } from '@ld.consumer-portal/models';
import { LoggingService, SeverityLevel, type PayoffDateConstraints, resolveDateConstraintStrategy } from '@ld.consumer-portal/utils';
import { firstValueFrom, retry, timer } from 'rxjs';

const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
};

const STORAGE_KEYS = {
  LOANS_NOT_ELIGIBLE: 'LoansNotEligibleForPayoffRequest',
  LOANS_REQUESTED: 'loansRequestedForPayoffQuote',
};

@Injectable({
  providedIn: 'root',
})
export class PayoffQuoteService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  readonly isLoading = signal(false);

  readonly error = signal<string | null>(null);

  readonly isSuccess = signal(false);

  readonly confirmationNumber = signal<string | null>(null);

  readonly isRetryExhausted = signal(false);

  readonly showIneligiblePopup = signal(false);

  readonly eligibilityVersion = signal(0);

  private get baseUrl(): string {
    return this.env.appSettings?.bkfsIntegrationUrl || this.env.appSettings?.ServicingLINCPortalPayments || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings?.apiNamespace || 'api/v1';
  }

  getDateConstraints(propertyState?: string, deliveryMethod?: string): PayoffDateConstraints {
    const strategy = resolveDateConstraintStrategy(propertyState);
    return strategy.getConstraints((deliveryMethod as 'SecureMessage' | 'Fax' | 'Mail') || 'SecureMessage');
  }

  async requestPayoffQuote(request: PayoffQuoteRequest, encryptionValue: string): Promise<PayoffQuoteResponse> {
    this.isLoading.set(true);
    this.error.set(null);
    this.isSuccess.set(false);
    this.confirmationNumber.set(null);
    this.isRetryExhausted.set(false);

    try {
      const url = `${this.baseUrl}/${this.apiNamespace}/ServicingLINCPortalIntegration/PayoffQuote`;

      const params = new HttpParams().set('encryptionValue', encryptionValue);

      const body = {
        LoanNumber: request.loanNumber,
        PayoffDate: request.payoffDate,
        DeliveryMethod: this.normalizeDeliveryMethod(request.deliveryMethod),
        Email: request.email || '',
        FaxNumber: request.faxNumber || '',
        MailingAddress: request.mailingAddress || '',
      };

      const response = await firstValueFrom(
        this.http
          .post<{ success?: boolean; confirmationNumber?: string; errorMessage?: string }>(url, body, { params })
          .pipe(
            retry({
              count: RETRY_CONFIG.maxRetries,
              delay: (error: unknown, retryCount: number) => {
                const delay = Math.min(
                  RETRY_CONFIG.initialDelayMs * Math.pow(2, retryCount - 1),
                  RETRY_CONFIG.maxDelayMs
                );
                this.loggingService.logWarning(
                  `Payoff quote request failed, retrying (${retryCount}/${RETRY_CONFIG.maxRetries})`,
                  {
                    loanNumber: request.loanNumber,
                    retryCount,
                    delayMs: delay,
                    errorMessage: error instanceof Error ? error.message : 'Unknown error',
                  }
                );
                return timer(delay);
              },
            })
          )
      );

      if (response?.success !== false) {
        this.isSuccess.set(true);
        this.confirmationNumber.set(response?.confirmationNumber || 'CONFIRMED');
        this.markLoanAsRequested(request.loanNumber);
        this.loggingService.logEvent('PayoffQuoteRequestSuccess', {
          loanNumber: request.loanNumber,
          confirmationNumber: response?.confirmationNumber || 'CONFIRMED',
          deliveryMethod: body.DeliveryMethod,
        });
        return {
          success: true,
          confirmationNumber: response?.confirmationNumber,
        };
      } else {
        const errorMessage = response?.errorMessage || 'Failed to request payoff quote';
        this.error.set(errorMessage);
        this.loggingService.logWarning('Payoff quote request returned failure response', {
          loanNumber: request.loanNumber,
          errorMessage,
        });
        return {
          success: false,
          errorMessage: response?.errorMessage,
        };
      }
    } catch (err) {
      this.isRetryExhausted.set(true);
      const errorMessage = 'Unable to request payoff quote due to technical challenges. Please try again later.';
      this.loggingService.logException(
        this.loggingService.getCustomizedError(
          err,
          `Payoff quote request failed for loan ${request.loanNumber} after ${RETRY_CONFIG.maxRetries} retries`
        ),
        SeverityLevel.Error
      );
      this.loggingService.logEvent('PayoffQuoteRequestError', {
        loanNumber: request.loanNumber,
        errorMessage: err instanceof Error ? err.message : String(err),
        retryExhausted: true,
      });
      this.error.set(errorMessage);
      return {
        success: false,
        errorMessage,
        isRetryExhausted: true,
      };
    } finally {
      this.isLoading.set(false);
    }
  }

  checkEligibility(loanNumber: string, loanType?: string, canRequestFromBackend?: boolean): PayoffEligibility {
    if (canRequestFromBackend === false) {
      return {
        canRequestPayoffQuote: false,
        reason: 'This loan is not eligible for payoff quote requests.',
      };
    }

    if (loanType?.toLowerCase() === 'heloc') {
      return {
        canRequestPayoffQuote: false,
        reason: 'HELOC loans are not eligible for payoff quote requests.',
      };
    }

    if (this.hasAlreadyRequested(loanNumber)) {
      return {
        canRequestPayoffQuote: false,
        reason: 'A payoff quote has already been requested for this loan.',
      };
    }

    if (this.isMarkedNotEligible(loanNumber)) {
      return {
        canRequestPayoffQuote: false,
        reason: 'This loan is not eligible for payoff quote requests.',
      };
    }

    return { canRequestPayoffQuote: true };
  }

  hasAlreadyRequested(loanNumber: string): boolean {
    const requested = localStorage.getItem(STORAGE_KEYS.LOANS_REQUESTED);
    return requested?.includes(loanNumber) || false;
  }

  isMarkedNotEligible(loanNumber: string): boolean {
    const notEligible = localStorage.getItem(STORAGE_KEYS.LOANS_NOT_ELIGIBLE);
    return notEligible?.includes(loanNumber) || false;
  }

  markLoanAsRequested(loanNumber: string): void {
    this.addToStorageList(loanNumber, STORAGE_KEYS.LOANS_REQUESTED);
  }

  markLoanAsNotEligible(loanNumber: string): void {
    this.addToStorageList(loanNumber, STORAGE_KEYS.LOANS_NOT_ELIGIBLE);
    this.eligibilityVersion.update((v) => v + 1);
  }

  getLoansToExclude(): string[] {
    const notEligible = localStorage.getItem(STORAGE_KEYS.LOANS_NOT_ELIGIBLE) || '';
    const requested = localStorage.getItem(STORAGE_KEYS.LOANS_REQUESTED) || '';
    const combined = [notEligible, requested].filter(Boolean).join(',');
    return combined ? combined.split(',') : [];
  }

  reset(): void {
    this.isLoading.set(false);
    this.error.set(null);
    this.isSuccess.set(false);
    this.confirmationNumber.set(null);
    this.isRetryExhausted.set(false);
  }

  private addToStorageList(loanNumber: string, key: string): void {
    let current = localStorage.getItem(key);
    if (!current) {
      current = loanNumber;
    } else if (!current.includes(loanNumber)) {
      current = `${current},${loanNumber}`;
    } else {
      return;
    }
    localStorage.setItem(key, current);
  }

  private normalizeDeliveryMethod(method: string): string {
    const methodMap: Record<string, string> = {
      SecureMessage: 'secureMessage',
      Fax: 'fax',
      Mail: 'mail',
    };
    return methodMap[method] || method.toLowerCase();
  }
}
